import { create } from 'zustand';
import {
  filterByDateRange,
  filterByLangValue,
  sortByCost,
  sortByLangValue,
  sortByName,
} from '../features/table/helpers';
import { formatToBackDateTimeWithOffset } from '../helpers/date-time';
import { PaginationType } from '../types/pagination';
import {
  deletePromo,
  fetchPromo,
  fetchPromoList,
  FetchPromoListRequest,
  postPromo,
  PromoStatusType,
  PromotionType,
  putPromo,
  restorePromo,
} from './api/promotion-api';
import { useAlertStore } from './use-alert-store';
import dayjs from 'dayjs';

interface Store extends Partial<PaginationType> {
  postPromo: () => Promise<void>;
  putPromo: () => Promise<void>;
  fetchPromo: (id: string) => Promise<void>;
  fetchPromoList: (params: FetchPromoListRequest) => Promise<void>;
  deletePromo: (id: string) => Promise<void>;
  restorePromo: (id: string) => Promise<void>;
  handleUpdatePromo: (data: Partial<PromotionType>) => void;
  handleResetPromo: () => void;
  isLoadingFetch?: boolean;
  isLoading?: boolean;
  list?: PromotionType[];
  promo?: Partial<PromotionType>;
}

export const usePromotionStore = create<Store>((set, get) => ({
  handleUpdatePromo: (data) => {
    set((store) => ({ ...store, promo: { ...store.promo, ...data } }));
  },
  handleResetPromo: () => {
    set((store) => ({ ...store, promo: undefined }));
  },
  fetchPromoList: async (params) => {
    set({ isLoading: true });
    try {
      const { promotions, ...other } = await fetchPromoList(params);
      let list = [...promotions].map((item) =>
        dayjs(new Date()).isBefore(item.active_from)
          ? { ...item, status: 'Planned' as PromoStatusType }
          : item,
      );

      if (params.name) {
        list = list.filter((item) => filterByLangValue(item.name, params.name));
      }

      if (params.description) {
        list = list.filter((item) => filterByLangValue(item.description, params.description));
      }

      if (params.active_from) {
        list = filterByDateRange(list, params.active_from, 'active_from');
      }

      if (params.active_to) {
        list = filterByDateRange(list, params.active_to, 'active_to');
      }

      if (params.status) {
        const possibleStatuses = params.status.split(',');
        list = list.filter((item) => possibleStatuses.includes(item.status));
      }

      if (params.field == 'id') {
        list = list.sort((a, b) => sortByCost(a.id, b.id, params.dir!));
      }

      if (params.field == 'name') {
        list = list.sort((a, b) => sortByLangValue(a.name, b.name, params.dir!));
      }
      if (params.field == 'description') {
        list = list.sort((a, b) => sortByLangValue(a.description, b.description, params.dir!));
      }

      if (params.field == 'active_from') {
        list = list.sort((a, b) => sortByName(a.active_from, b.active_from, params.dir!));
      }

      if (params.field == 'active_to') {
        list = list.sort((a, b) => sortByName(a.active_to, b.active_to, params.dir!));
      }

      set({ list, ...other });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по акциям', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  fetchPromo: async (id) => {
    set({ isLoading: true });
    try {
      const resp = await fetchPromo(id);
      set({ promo: resp });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по акции', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  postPromo: async () => {
    set({ isLoading: true });
    try {
      const promo = get().promo;
      if (!promo) {
        throw new Error('Promo is not defined');
      }
      const resp = await postPromo({
        ...promo,
        active_from: formatToBackDateTimeWithOffset(promo.active_from),
        active_to: formatToBackDateTimeWithOffset(promo.active_to),
        status: 'Active',
      });
      useAlertStore.getState().add({ label: 'Акция успешно добавлена', variant: 'success' });
      set((store) => ({ ...store, list: [resp, ...(store.list || [])] }));
      set({ promo: resp });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить данные по акции', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  putPromo: async () => {
    set({ isLoading: true });
    try {
      const promo = get().promo;
      if (!promo) {
        throw new Error('Promo is not defined');
      }
      const resp = await putPromo({
        ...promo,
        active_from: formatToBackDateTimeWithOffset(promo.active_from),
        active_to: formatToBackDateTimeWithOffset(promo.active_to),
      });
      useAlertStore.getState().add({ label: 'Акция успешно обновлена', variant: 'success' });
      set((store) => ({
        ...store,
        list: store.list?.map((item) => (item.id === resp.id ? resp : item)),
      }));
    } catch (e) {
      console.error(e);
      // useAlertStore
      //   .getState()
      //   .add({ label: 'Не удалось обновить данные по акции', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  deletePromo: async (id) => {
    set({ isLoading: true });
    try {
      await deletePromo(id);
      useAlertStore.getState().add({ label: 'Акция успешно удалена', variant: 'success' });
      set((store) => ({
        ...store,
        list: store.list?.map((item) =>
          String(item.id) === id ? { ...item, is_deleted: true } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось удалить акцию', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  restorePromo: async (id) => {
    set({ isLoading: true });
    try {
      await restorePromo(id);
      useAlertStore.getState().add({ label: 'Акция успешно восстановлена', variant: 'success' });
      set((store) => ({
        ...store,
        list: store.list?.map((item) =>
          String(item.id) === id ? { ...item, is_deleted: false } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось восстановить акцию', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
}));
